// design/theme.js

import { createTheme, styled } from '@mui/material/styles';

// Theme constants
export const themeConstants = {
  // Font tokens — sans is in MUI's typography.fontFamily; mono is for
  // tabular numerics (IDs, miles, money, timestamps).
  fonts: {
    mono: '"JetBrains Mono", "SF Mono", ui-monospace, Menlo, monospace',
  },
  // Border radius tokens - clean spreadsheet style
  borderRadius: {
    xs: 2,      // Small pills, chips
    sm: 4,      // Buttons, inputs (reduced for cleaner look)
    md: 8,      // Small cards, tables
    lg: 12,     // Standard cards
    xl: 16,     // Hero cards, modals
  },
  colors: {
    // Unified background colors (Velles-style)
    background: {
      page: '#f8fafc',        // Page background (slate-50)
      card: '#ffffff',        // Card backgrounds
      input: '#f1f5f9',       // Input fields (slate-100)
      inputHover: '#e2e8f0',  // Input hover (slate-200)
    },
    // Border colors
    border: {
      default: '#e2e8f0',     // Default borders (slate-200)
      table: '#cbd5e1',       // Table cell borders (slate-300)
      hover: '#94a3b8',       // Hover borders (slate-400)
    },
    input: {
      background: '#ffffff',      // White background to stand out
      backgroundHover: '#fafafa', // Very subtle hover
      backgroundFocused: '#ffffff', // White when focused
      border: '#d1d5db',          // Visible border (gray-300)
      borderHover: '#9ca3af',     // Hover border (gray-400)
      borderFocus: '#3b82f6',     // Focus border (blue-500)
    },
    section: {
      titleBackground: '#3b6089',  // Primary section title background
      license: '#c2804b',          // License section
      assignment: '#7a529b',       // Assignment section
      employment: '#658f81',       // Employment section
      additional: '#6588ae',       // Additional info section
      payRate: '#d68634',          // Pay rate section
    },
    status: {
      available: '#7fb37f',    // Green
      unavailable: '#FA5457',  // Red/Coral
      home: '#b2b7c2',         // Neutral gray
      dispatched: 'oklch(0.52 0.16 305)',  // Purple — matches Stellar lightness/chroma
      inTransit: 'oklch(0.52 0.13 235)',   // Stellar --status-info (light theme)
      assigned: '#89cdcf',                 // Light teal
      delivered: 'oklch(0.52 0.16 155)',   // Stellar --status-ok (light theme)
    },
    // Employment status colors
    employment: {
      active: '#7fb37f',       // Green - matches status.available
      applicant: '#01B4BC',    // Teal
      terminated: '#FA5457',   // Red/Coral - matches status.unavailable
    },
    // ELD (Electronic Logging Device) status colors - block style
    eldStatus: {
      driving: '#7fb37f',      // Green - actively driving
      sleeper: '#f89a2d',      // Orange - sleeper berth
      onDuty: '#4d9bef',       // Blue - on duty not driving
      offDuty: '#b2b7c2',      // Neutral gray - off duty
      yardMove: '#c2edaf',     // Light green - yard moves
      personalUse: '#F6D51F',  // Yellow - personal conveyance
    },
    text: {
      primary: '#163866',       // Deep navy blue
      secondary: '#585858',
      heading: '#3e3e3e',       // Dark gray for headings/titles, table headers
      tertiary: '#fff',         // White text on colored backgrounds
      muted: '#6b7280',         // Gray-500: table headers, labels, subdued text
      placeholder: '#9ca3af',   // Gray-400: placeholders, very subdued elements
      accent: '#0e67e7',        // Link color
      accentSecondary: '#0056b3', // Secondary accent (section titles, dates, etc.)
      accentTertiary: '#4681c0',  // Tertiary accent (map icons, etc.)
    },
    header: {
      pay: '#7fb37f',           // Green - for pay items
      deduction: '#FA5457',     // Red/Coral - for deduction items
    },
    utility: {
      rowHover: '#e8f0f8',           // Light blue tint for row hover
      rowCompleted: '#e5fff0',       // Light green tint for completed/delivered rows
      backgroundLight: '#f8fafc',    // Unified background
      tableHeader: '#f1f5f9',        // Light neutral header (slate-100)
      error: '#FA5457',              // Red/Coral
      warning: '#f89a2d',            // Orange
      success: '#7fb37f',            // Green
    },
    // Snackbar colors - vibrant palette
    snackbar: {
      success: {
        background: '#7fb37f', // Green
        color: '#ffffff',
      },
      error: {
        background: '#FA5457', // Red/Coral
        color: '#ffffff',
      },
      info: {
        background: '#4d9bef', // Blue
        color: '#ffffff',
      },
      warning: {
        background: '#f89a2d', // Orange
        color: '#ffffff',
      }
    },
    // Sidebar navigation colors
    sidebar: {
      background: '#0f172a',        // Dark navy (slate-900)
      backgroundHover: '#1e293b',   // Slightly lighter (slate-800)
      text: '#cbd5e1',              // Menu item rest (slate-300) — lightened from slate-400 for brighter, more legible items
      textHover: '#f1f5f9',         // Light text on hover (slate-100) — bumped so rest→hover step stays distinct
      textActive: '#ffffff',        // White text when active
      accent: '#4d9bef',            // Blue accent
      accentHover: '#3d8bdf',       // Darker blue on hover
      divider: '#1e293b',           // Divider color (slate-800)
      iconRail: '#020617',          // Darker icon rail (slate-950)
    },
  },
  highlights: {
    cell: {
      negative: { backgroundColor: '#ffe2e2' }, // Light red for negative values
      positive: { backgroundColor: '#e6ffe6' }, // Light green for positive values
      warning: { backgroundColor: '#fff9e6' },  // Light yellow for warnings
      info: { backgroundColor: '#e6f7ff' }      // Light blue for information
    }
  },
  typography: {
    fontSize: {
      xxs: '0.65rem',   // 10.4px - chips, micro labels
      xs: '0.75rem',    // 12px - captions, secondary text
      sm: '0.813rem',   // 13px - compact UI elements
      base: '0.875rem', // 14px - default body text
      md: '1rem',       // 16px - emphasis text
      lg: '1.1rem',     // 17.6px - section values, headings
    }
  },
  spacing: {
    base: 8, // 8px base spacing unit
  },
  // Velles-style soft shadows
  shadows: {
    none: 'none',
    sm: '0 1px 2px rgba(0,0,0,0.05)',
    md: '0 2px 8px rgba(0,0,0,0.08)',
    lg: '0 4px 16px rgba(0,0,0,0.10)',
    input: 'inset 0 1px 2px rgba(0,0,0,0.06)',
    inputFocused: 'inset 0 1px 2px rgba(0,0,0,0.06), 0 0 0 2px #bfdbfe',
    card: '0 2px 8px rgba(0,0,0,0.08)',
  }
};

// Helper for replacing hardcoded colors in your components
export const colorMappings = {
  // UI Backgrounds
  '#3f78b7': themeConstants.colors.section.titleBackground,  
  '#c2804b': themeConstants.colors.section.license,
  '#7a529b': themeConstants.colors.section.assignment,
  '#658f81': themeConstants.colors.section.employment,
  '#6588ae': themeConstants.colors.section.additional,
  '#d68634': themeConstants.colors.section.payRate,
  
  // Status Colors
  '#6b9e59': themeConstants.colors.status.available,
  '#e05555': themeConstants.colors.status.unavailable,
  '#887676': themeConstants.colors.status.home,
  '#5b9dde': themeConstants.colors.status.dispatched,
  '#dc8d44': themeConstants.colors.status.inTransit,
  '#7d9ba6': themeConstants.colors.status.assigned,
  
  // Text colors
  '#585858': themeConstants.colors.text.secondary,
  '#163866': themeConstants.colors.text.primary,
  '#fff': themeConstants.colors.text.tertiary,
  
  // Utility colors
  '#f0f8ff': themeConstants.colors.utility.rowHover,
  '#f4f4f4': themeConstants.colors.utility.backgroundLight,
  '#f9f9f9': themeConstants.colors.utility.tableHeader,
};

// Design utility functions
export const createStyledVariant = (BaseComponent, customStyles) => {
  return styled(BaseComponent)(({ theme }) => ({
    ...customStyles(theme),
  }));
};

// Destructure themeConstants to separate spacing and shadows from the rest
const { spacing: spacingConfig, shadows: customShadows, ...restConstants } = themeConstants;

// MUI theme
export const theme = createTheme({
  // Merge the rest of the constants
  ...restConstants,
  // Set spacing correctly using the base value
  spacing: spacingConfig.base,
  // Provide a default MUI shadows array
  shadows: createTheme({}).shadows, // Use default MUI shadows
  palette: {
    primary: {
      main: '#4d9bef',        // Blue
      contrastText: '#ffffff',
    },
    success: {
      main: '#7fb37f',        // Green
      contrastText: '#ffffff',
    },
    error: {
      main: '#FA5457',        // Red/Coral
      contrastText: '#ffffff',
    },
    background: {
      default: '#f8fafc',     // Updated to slate-50
      paper: '#ffffff',
    },
    text: {
      primary: '#163866',     // Deep navy blue
      secondary: '#64748b',   // slate-500
    },
  },
  typography: {
    fontFamily: [
      'Inter',
      'ui-sans-serif',
      'system-ui',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    fontWeightRegular: 500,
  },
  components: {
    MuiContainer: {
      styleOverrides: {
        root: {
          paddingLeft: '16px !important',  // UPDATED: 16px instead of 32px
          paddingRight: '16px !important', // UPDATED: 16px instead of 32px
          '@media (min-width:900px)': {
            paddingLeft: '16px !important',  // UPDATED: 16px instead of 32px
            paddingRight: '16px !important', // UPDATED: 16px instead of 32px
          },
          '& > .MuiBox-root': {
            paddingLeft: '0 !important',
            paddingRight: '0 !important',
            '@media (min-width:600px)': {
              paddingLeft: '0 !important',
              paddingRight: '0 !important',
            },
          },
        },
      },
    },

    // Normal table cells
    MuiTableCell: {
      styleOverrides: {
        root: {
          fontFamily: 'Inter, ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
          fontWeight: 500,
          fontSize: '0.78rem',               // Increased for better readability
          lineHeight: '1.3',                 // Compact: reduced from 1.4
          display: 'table-cell',
          verticalAlign: 'inherit',
          borderBottom: `1px solid ${themeConstants.colors.border.table}`,
          textAlign: 'left',
          padding: '8px',                            // 8px all sides
          color: themeConstants.colors.text.primary,
        },
      },
    },

    // Table header cells - white background with muted gray text
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          '& .MuiTableCell-root': {
            color: themeConstants.colors.text.heading,
            fontWeight: 500,
            height: '38px',                  // Increased for better spacing
            fontSize: '0.85rem',             // Increased for better readability
            borderBottom: `1px solid ${themeConstants.colors.border.table}`,
            backgroundColor: '#ffffff',
          },
        },
      },
    },
  },
});

// Utility styles for spacing and layout
export const utilityStyles = {
  commonSpacing: {
    sectionGap: { xs: 2, sm: 3 },
    contentPadding: { xs: 2, sm: 3, md: 4 },
  },

  flexStyles: {
    centerAll: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    centerVertical: {
      display: 'flex',
      alignItems: 'center',
    },
    spaceBetween: {
      display: 'flex',
      justifyContent: 'space-between',
    },
  }
};

// Global styles - Velles-style page background
export const globalStyles = {
  styleOverrides: `
    body {
      background-color: #f8fafc;
    }
  `,
};
